import React from "react";
import { Card, CardContent, Box, Typography, Chip, IconButton, Tooltip, TextField } from "@mui/material";
import DocCardImage from "../DocCardImage/DocCardImage";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { useTranslation } from "react-i18next";

const DocCard = ({
	title,
	href,
	onLinkClick,
	subtitle,
	chipLabel,
	chipProps,
	imageProps = {},
	rightContent,
	cardProps = {},
	linkProps = {},
	editableTitle = null,
}) => {
	const { t } = useTranslation();
	const resolvedTitle = title || t('GENERAL.UNTITLED');
	const [isEditing, setIsEditing] = React.useState(false);
	const hasEditableTitle = Boolean(editableTitle);

	React.useEffect(() => {
		if (!hasEditableTitle) {
			setIsEditing(false);
		}
	}, [hasEditableTitle]);

	const shouldRenderImage =
		imageProps &&
		(imageProps.path ||
			imageProps.url ||
			imageProps.filename ||
			imageProps.type);

	const computedTitle =
		(hasEditableTitle ? editableTitle?.value : resolvedTitle) || resolvedTitle;

	const handleClick = (event) => {
		if (onLinkClick) {
			event.preventDefault();
			onLinkClick(event);
		}
	};

	const handleStartEditing = (event) => {
		if (event) event.stopPropagation();
		if (!hasEditableTitle || editableTitle?.disabled) return;
		setIsEditing(true);
	};

	const handleFinishEditing = (shouldSubmit = true) => {
		if (!hasEditableTitle) return;
		setIsEditing(false);
		if (shouldSubmit && editableTitle?.onSubmit) {
			editableTitle.onSubmit();
		}
		if (!shouldSubmit && editableTitle?.onCancel) {
			editableTitle.onCancel();
		}
	};

	const renderTitleContent = () => {
		if (!hasEditableTitle) {
			if (href) {
				return (
					<Typography
						variant="body2"
						sx={{
							fontWeight: 500,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						<a
							href={href}
							target="_blank"
							rel="noreferrer"
							onClick={handleClick}
							style={{ textDecoration: "none" }}
							{...linkProps}
						>
							{computedTitle}
						</a>
					</Typography>
				);
			}

			return (
				<Typography
					variant="body2"
					sx={{
						fontWeight: 500,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{computedTitle}
				</Typography>
			);
		}

		if (isEditing) {
			return (
				<TextField
					autoFocus
					fullWidth
					size="small"
					value={editableTitle?.value || ""}
					onChange={(event) => editableTitle?.onChange?.(event.target.value)}
					onBlur={() => handleFinishEditing(true)}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							handleFinishEditing(true);
						} else if (event.key === "Escape") {
							handleFinishEditing(false);
						}
					}}
					placeholder={editableTitle?.placeholder || t('UI_TEXT.ENTER_NAME')}
				/>
			);
		}

		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.5,
					minWidth: 0,
				}}
			>
				<Typography
					variant="body2"
					sx={{
						fontWeight: 500,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{computedTitle}
				</Typography>
				<Tooltip title={editableTitle?.tooltip || t('UI_TEXT.RENAME_FILE')}>
					<IconButton
						size="small"
						onClick={handleStartEditing}
						sx={{ color: "text.secondary", flexShrink: 0 }}
					>
						<DriveFileRenameOutlineIcon fontSize="inherit" />
					</IconButton>
				</Tooltip>
			</Box>
		);
	};

	return (
		<Card variant="outlined" sx={{ width: "100%" }} {...cardProps}>
			<CardContent
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					py: 1.5,
					"&:last-child": { pb: 1.5 },
					gap: 2,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
					{shouldRenderImage && <DocCardImage {...imageProps} />}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						{renderTitleContent()}
						{subtitle && (
							<Typography variant="caption" color="text.secondary">
								{subtitle}
							</Typography>
						)}
					</Box>
				</Box>
				{rightContent ||
					(chipLabel ? (
						<Chip size="small" label={chipLabel} {...chipProps} />
					) : null)}
			</CardContent>
		</Card>
	);
};

export default DocCard;
